import { Bus, constants, DateService, IntlManager } from '@atlas/core';

import { MATCH_WINNER_SPORT_IDS } from '../constants';

const {
  STATUS: { ACTIVE },
} = constants;

const uriPattern = /^https?:\/\//i;
export const isUrlAbsolute = url => uriPattern.test(url);

export const compare = (a, b) => {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

export const CDN_TYPE_GAME = 'game';
export const CDN_TYPE_CMS = 'cms';
export const CDN_TYPE_SPORT = 'sport';
export const CDN_TYPE_CRM = 'crm';
export const CDN_TYPE_BASE = 'base';
export const CDN_TYPE_BILLING = 'billing';

const devCdnUrls = {
  [CDN_TYPE_BASE]: 'https://media.pl-test.cdn-platform.xyz',
  [CDN_TYPE_BILLING]: 'https://media.pl-test.cdn-platform.xyz/billing',
  [CDN_TYPE_CMS]: 'https://media.pl-test.cdn-platform.xyz/cms',
  [CDN_TYPE_CRM]: 'https://media.pl-test.cdn-platform.xyz/crm',
  [CDN_TYPE_GAME]: 'https://media.pl-test.cdn-platform.xyz/games',
  [CDN_TYPE_SPORT]: 'https://media.pl-test.cdn-platform.xyz/sport',
};

const stageCdnUrls = {
  [CDN_TYPE_BASE]: 'https://media.pl-stage1.cdn-platform.xyz',
  [CDN_TYPE_BILLING]: 'https://media.pl-stage1.cdn-platform.xyz/billing',
  [CDN_TYPE_CMS]: 'https://media.pl-stage1.cdn-platform.xyz/cms',
  [CDN_TYPE_CRM]: 'https://media.pl-stage1.cdn-platform.xyz/crm',
  [CDN_TYPE_GAME]: 'https://media.pl-stage1.cdn-platform.xyz/games',
  [CDN_TYPE_SPORT]: 'https://media.pl-stage1.cdn-platform.xyz/sport',
};

const prodCdnUrls = {
  [CDN_TYPE_BASE]: 'https://media.pl-01.cdn-platform.xyz',
  [CDN_TYPE_BILLING]: 'https://media.pl-01.cdn-platform.xyz/billing',
  [CDN_TYPE_CMS]: 'https://media.pl-01.cdn-platform.xyz/cms',
  [CDN_TYPE_CRM]: 'https://media.pl-01.cdn-platform.xyz/crm',
  [CDN_TYPE_GAME]: 'https://media.pl-01.cdn-platform.xyz/games',
  [CDN_TYPE_SPORT]: 'https://media.pl-01.cdn-platform.xyz/sport',
};

const getEnvCDN = () => {
  switch (process.env.WEBPACK_PROXY) {
    case 'stage1':
      return stageCdnUrls;
    case 'prod':
      return prodCdnUrls;
    default:
      return devCdnUrls;
  }
};

window.SERVICE_CDN = window.SERVICE_CDN || getEnvCDN();

const trimSlashStart = (str = '') => str.replace(/^\/+/g, '');

export const imageUrlCDN = (url, cdnType) => {
  const cdn = window.SERVICE_CDN[cdnType];
  const res = url || '';
  return [cdn, trimSlashStart(res)].join('/');
};
export const getCDNUrl = cdnType => {
  return window.SERVICE_CDN[cdnType];
};

export const getIsFieldRequired = rules => Boolean(rules.find(({ type }) => type === 'REQUIRED'));

export const generateErrorFromCode = (code, message = code, violationInfo = {}) => ({
  message,
  type: code,
  violationInfo,
});
export const handleFormError = (error, setFormError = () => {}) => {
  const { code, message, status, violationInfo } = error;

  // if (errors && errors.length) {
  // TODO: Implement multierrors format
  // }

  setFormError([generateErrorFromCode(code || status, code || message, violationInfo)]);
  throw error;
};
export const handleFormSubmit =
  (action, dataFormatter = value => value) =>
  async (formData, { setGlobalErrors, setIsLoading }) => {
    const formattedData = dataFormatter(formData);

    if (formattedData === null) {
      return;
    }

    try {
      setIsLoading(true);
      const result = await action(formattedData);

      setIsLoading(false);

      return result;
    } catch (error) {
      setIsLoading(false);
      handleFormError(error, setGlobalErrors);
    }
  };

// responsible gaming limits
export const getLimitWarning = (data, type) => {
  if (!data) {
    return null;
  }

  const { dateFrom, value } = data;

  return {
    message: value ? `${type}_limits_were_edited` : 'limits_edit_nullify',
    params: {
      date: DateService.getFormattedDateWithTime(dateFrom),
      value: value && IntlManager.formatMoney(value),
    },
  };
};

export const getSelfRegulationLimitsFromFields = (fields, spentType) =>
  Object.entries(fields).reduce((acc, [key, value]) => {
    const parsedValue = Number.parseFloat(value) || null;

    if (!Number.isFinite(parsedValue)) {
      const item = {
        type: key.toUpperCase(),
        value: 0,
      };

      if (spentType) {
        item.spentType = spentType;
      }

      return [...acc, item];
    }

    const item = {
      type: key.toUpperCase(),
      value: parsedValue,
    };

    if (spentType) {
      item.spentType = spentType;
    }

    return [...acc, item];
  }, []);

export const getSelfRegulationFormattedFields = (spentType, formPayload) => {
  const limits = getSelfRegulationLimitsFromFields(formPayload, spentType);

  if (limits === null) {
    return null;
  }

  return { limits };
};

export const getParsedFormErrorParameters = params => {
  return Object.entries(params).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: key === 'limit' ? IntlManager.formatMoney(value) : value,
    }),
    {},
  );
};

export const getDayStart = date => date.startOf('day').unix() * 1000;

export const getDayEnd = date => date.endOf('day').unix() * 1000;
export const slugify = str => str;

export const loadScript = src =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.onload = resolve;
    script.onerror = reject;
    script.src = src;
    document.head.append(script);
  });

export function getConfig(name, defaultValue = null) {
  return window.SYS_CONFIG?.[name] ?? defaultValue;
}

export class LocalConfig {
  static get(name, defaultValue = null) {
    return getConfig(name, defaultValue);
  }

  static is(name, right) {
    const left = getConfig(name);
    return left === right;
  }
}

/* TODO: Temporary solution until the icons are set via the backend ||
       very poor solution but necessary for now  */

export const onErrorFlagHandler = async ({ regionId, target }) => {
  if (regionId) {
    target.src = imageUrlCDN(`svg-icons/flags/${regionId}.svg`, CDN_TYPE_BASE);
  } else {
    target.src = imageUrlCDN('svg-icons/flags/default.svg', CDN_TYPE_BASE);
  }
};

export const get1X2Outcomes = outcomes => ({
  awayOdds: outcomes.find(outcome => outcome.type === '{participant2}')?.odds,
  drawOdds: outcomes.find(outcome => outcome.type === 'X')?.odds,
  homeOdds: outcomes.find(outcome => outcome.type === '{participant1}')?.odds,
});

export const getMatchWinnerOutcomes = marketTypes => {
  const matchWinnerId = Object.keys(marketTypes).find(id =>
    MATCH_WINNER_SPORT_IDS.includes(Number(id)),
  );
  const markets = marketTypes[matchWinnerId]?.markets;
  const matchWinnerOutcomes =
    markets && Object.values(markets).find(market => market.status === ACTIVE)?.outcomes;

  return get1X2Outcomes(matchWinnerOutcomes ? Object.values(matchWinnerOutcomes) : []);
};

export const kebabCase = str => {
  const re = /([0-9]+|([A-Z][a-z]+)|[a-z]+|([A-Z]+)(?![a-z]))/g;

  return (String(str ?? '').match(re) || []).map(x => x.toLowerCase()).join('-');
};

/**
 * Capitalizes the first letter of each word in a string.
 *
 * - Works with one or multiple words
 * - Handles multiple spaces between words
 *
 * @param {string} str - Input string
 * @returns {string} String with each word capitalized
 *
 * @example
 * capitalize("hello");
 * // "Hello"
 *
 * @example
 * capitalize("hello world");
 * // "Hello World"
 */
export const capitalize = str => {
  return str
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const uniq = array => [...new Set(array)];

export const camelCase = string =>
  string
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, '');

export const isFunction = func => {
  return typeof func === 'function';
};

export const sportsbookRouterListenerCb = state => Bus.emit(Bus.events.sport.urlState, state);

export const isLocalEnv = window.SYS_CONFIG.env === 'local';

export const transformParameters = parametersArray => {
  const result = {};

  parametersArray.forEach(paramObj => {
    const { parameterName, possibleValues } = paramObj;

    result[parameterName] = Object.entries(possibleValues).map(([value, text]) => ({
      text,
      value,
    }));
  });

  return result;
};
